import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../AuthContext';

// Step 31: the grant-database page remembers where the user left it. Every
// piece of screen state is written to the browser's own storage the moment
// it changes and read back as the starting value when the page opens.
// Keys are "GrantDB:<uid>:<name>", so each signed-in user has their own
// memory. Nothing here talks to a server. Every read and write is guarded:
// storage may be missing, full, or blocked (private windows).

const PREFIX = 'GrantDB:';
const ANONYMOUS = 'anonymous';

function storage() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch (e) {
    return null;
  }
}

// The stored value, or undefined when there is none or it cannot be read.
export function read(key) {
  try {
    const store = storage();
    if (!store) return undefined;
    const raw = store.getItem(key);
    if (raw == null) return undefined;
    return JSON.parse(raw);
  } catch (e) {
    return undefined;
  }
}

// Stores the value; true when it was stored, false when it could not be.
export function write(key, value) {
  try {
    const store = storage();
    if (!store) return false;
    if (value === undefined) {
      store.removeItem(key);
      return true;
    }
    store.setItem(key, JSON.stringify(value));
    return true;
  } catch (e) {
    return false;
  }
}

export function remove(key) {
  try {
    const store = storage();
    if (store) store.removeItem(key);
    return true;
  } catch (e) {
    return false;
  }
}

export function memoryKey(uid, name) {
  return `${PREFIX}${uid || ANONYMOUS}:${name}`;
}

// The signed-in user's id for the key, or "anonymous" until it is known.
export function useMemoryUid() {
  const { user } = useAuth();
  return (user && user.uid) || ANONYMOUS;
}

export function useMemoryKey(name) {
  return memoryKey(useMemoryUid(), name);
}

function sameValue(a, b) {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch (e) {
    return a === b;
  }
}

// Like useState, but the value starts from storage and every change is
// written back. When the key changes (the user's id arrives, or a different
// user signs in) the stored value for the new key is read and takes over;
// the old value is never written over it.
export function useRemembered(key, initial) {
  const [value, setValue] = useState(() => {
    const stored = read(key);
    return stored === undefined ? initial : stored;
  });
  const readyKey = useRef(key);
  const skipWrite = useRef(false);
  const latest = useRef(value);
  latest.current = value;

  useEffect(() => {
    if (readyKey.current === key) return;
    const stored = read(key);
    readyKey.current = key;
    if (stored !== undefined && !sameValue(stored, latest.current)) {
      skipWrite.current = true;
      setValue(stored);
    }
  }, [key]);

  useEffect(() => {
    if (readyKey.current !== key) return;
    if (skipWrite.current) {
      skipWrite.current = false;
      return;
    }
    write(key, value);
  }, [key, value]);

  return [value, setValue];
}
